<template>
  <div class="box" v-if="orderToEdit">
    <InfoautoCustomerFinder
      :departamentoId="order.departamentoId"
      @selectedCustomer="selectedInfoautoCustomer"
      v-if="showInfoautoSearch"
      @close="showInfoautoSearch = false"
    ></InfoautoCustomerFinder>
    <NewCustomerModal
      v-if="showNewCustomerModal"
      @close="showNewCustomerModal = false"
    />
    <ManageResaleTaxModal
      v-if="showManageResaleTax"
      :customerEmail="order.customerEmail"
      :customerName="order.customerInfoauto?.generalInfo.name || ''"
      :customerInfoautoId="order.customerInfoautoId"
      @close="showManageResaleTax = false"
      @resaleTaxCreated="resaleTaxCreated"
    ></ManageResaleTaxModal>
    <div class="box-header">
      <h6 class="title">
        {{ $t("message.order.general_info") }}
      </h6>
      <div class="header-actions">
        <!-- A quote is not in the store, nor packed or shipped: only its PDF -->
        <a
          v-if="!isQuote"
          class="header-action"
          :href="orderWebURL"
          title="View in store"
          target="_blank"
        >
          <i class="mdi mdi-eye-outline"></i>
        </a>
        <a
          class="header-action"
          :href="isQuote ? quoteURL : orderURL"
          :title="isQuote ? $t('message.order.print_quote') : 'Print invoice'"
          target="_blank"
        >
          <i class="mdi mdi-printer"></i>
        </a>
        <a
          v-if="!isQuote"
          class="header-action"
          :href="orderPackingSlipURL"
          title="Packing Slip"
          target="_blank"
        >
          <i class="mdi mdi-file-document-outline"></i>
        </a>
        <a
          v-if="!isQuote"
          class="header-action"
          :href="orderPackingManifestURL"
          :title="$t('message.order.packing_manifest')"
          target="_blank"
        >
          <i class="mdi mdi-package-variant-closed"></i>
        </a>
        <!-- The quote link for the customer, the same one the SMS sends -->
        <button
          v-if="isQuote"
          type="button"
          class="header-action"
          :class="{ 'is-done': copiedLink === 'customer' }"
          :title="$t('message.order.copy_customer_link')"
          @click="copyLink(quoteURL, 'customer')"
        >
          <i
            class="mdi"
            :class="
              copiedLink === 'customer' ? 'mdi-check' : 'mdi-share-variant'
            "
          ></i>
        </button>
        <button
          type="button"
          class="header-action"
          :class="{ 'is-done': copiedLink === 'admin' }"
          :title="$t('message.order.copy_order_link')"
          @click="copyLink(orderAdminLink, 'admin')"
        >
          <i
            class="mdi"
            :class="copiedLink === 'admin' ? 'mdi-check' : 'mdi-link-variant'"
          ></i>
        </button>
      </div>
    </div>
    <div class="box-content-block">
      <div class="info-box">
        <table class="first-dark">
          <tbody>
            <tr class="font-bold text-xl" v-if="getDepartments.length > 0">
              <td>
                {{ $t("message.order.store") }}
              </td>
              <td>
                <select
                  v-if="canBeEdited && canChangeDepartment"
                  name="store"
                  class="regular full bordered"
                  v-model.lazy="orderToEdit.departamentoId"
                >
                  <option
                    v-for="department in getDepartments"
                    :key="department.code"
                    :value="department.full.departamentoId"
                    >{{ department.name }}
                  </option>
                </select>
                <div v-else>
                  {{ orderToEdit.departamentoNombre }}
                </div>
              </td>
            </tr>
            <tr v-if="warehouses.length > 0">
              <td>
                {{ $t("message.order.warehouse") }}
              </td>
              <td>
                <select
                  v-if="canBeEdited && canChangeWarehouse"
                  name="warehouse"
                  class="regular full bordered"
                  v-model.lazy="orderToEdit.warehouseId"
                >
                  <option
                    v-for="warehouse in warehouses"
                    :key="warehouse.depositoId"
                    :value="warehouse.depositoId"
                    >{{ warehouse.descDeposito }}
                  </option>
                </select>
                <div v-else>
                  {{ orderWarehouseName }}
                </div>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.order.create_date") }}
              </td>
              <td>
                <input
                  class="regular full bordered"
                  v-if="canBeEdited"
                  type="text"
                  name="date"
                  v-model="orderToEdit.createDate"
                  disabled
                />
                <template v-else>
                  {{ orderToEdit.createDate | dateWithHours }}
                </template>
              </td>
            </tr>
            <tr v-if="orderToEdit.creatorInfoautoId !== null">
              <td>
                {{ $t("message.order.created_by") }}
              </td>
              <td>
                <b>
                  {{
                    `${orderToEdit.creatorName} ${orderToEdit.creatorSurname}`
                  }}
                </b>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.order.salesman") }}
              </td>
              <td>
                <select
                  v-if="
                    (canBeEdited &&
                      (order.status == 'Draft' ||
                        currentUser.role == 'superadmin')) ||
                      currentUserPermissions.includes('change_order_seller')
                  "
                  name="salesman"
                  class="regular full bordered"
                  v-model.lazy="orderToEdit.salesman"
                >
                  <option
                    v-for="seller in sellers"
                    :key="seller.id"
                    :value="seller.email"
                    >{{ seller.name }}
                  </option>
                </select>
                <template v-else>
                  {{ order.salesman }}
                </template>
              </td>
            </tr>
            <tr
              v-if="!orderToEdit.customerInfoauto && !order.customerInfoautoId"
            >
              <td>
                {{ $t("message.order.name") }}
              </td>
              <td v-if="!canBeEdited">
                <template
                  v-if="
                    order.customer &&
                      order.customer.generalInfo &&
                      order.customer.generalInfo.name
                  "
                >
                  {{ order.customer.generalInfo.name }}
                </template>
              </td>
              <td v-else-if="orderToEdit.customer">
                {{ orderToEdit.customer.generalInfo.name }}
              </td>
              <td v-else>
                {{ $t("message.order.no_customer_selected") }}
              </td>
            </tr>
            <tr v-if="!order.customerInfoauto && order.customer">
              <td>{{ $t("message.order.web_customer_taxable") }}</td>
              <td>
                <p class="text-red-600">
                  {{
                    order.customer.generalInfo.tax == "1"
                      ? $t("message.order.taxable")
                      : $t("message.order.no_taxable")
                  }}
                </p>
              </td>
            </tr>
            <tr v-if="!orderToEdit.customerInfoauto">
              <td>
                {{ $t("message.order.email") }}
              </td>
              <td>
                <input
                  class="regular full bordered"
                  v-if="canBeEdited"
                  type="text"
                  name="email"
                  list="emails"
                  v-model="tempEmail"
                  disabled
                  @input="getCustomers()"
                  @blur="orderToEdit.customerEmail = tempEmail"
                />
                <div v-else class="flex flex-col">
                  {{ orderToEdit.customerEmail }}
                  <p v-if="order && existingCustomer" class="ml-2">
                    ({{ $t("message.order.verified") }} -
                    {{ order.customer.generalInfo.priceLevel }})
                  </p>
                  <p v-else class="ml-2">({{ $t("message.order.guest") }})</p>
                </div>
                <datalist id="emails">
                  <option
                    v-for="customer in customers"
                    :key="customer.email"
                    :value="customer.email"
                  >
                    {{ customer.email.toLowerCase() }}
                    {{ customer.companyName }} {{ customer.name }}
                    {{ customer.surname }}
                  </option>
                </datalist>
                <button
                  type="button"
                  class="finder-link"
                  v-if="canBeEdited"
                  @click="showInfoautoSearch = true"
                >
                  <i class="mdi mdi-account-search"></i>
                  {{ $t("message.order.show_infoauto_finder") }}
                </button>
              </td>
            </tr>
            <tr>
              <td>{{ $t("message.order.purchase_order") }}</td>
              <td>
                <input
                  class="regular full bordered"
                  v-if="canBeEdited"
                  type="text"
                  placeholder="purchase order"
                  name="purchase-order"
                  v-model.lazy="orderToEdit.purchaseOrderNumber"
                />
                <template v-else>
                  {{
                    orderToEdit.purchaseOrderNumber ||
                      $t("message.order.not_provided")
                  }}
                </template>
              </td>
            </tr>
            <tr>
              <td>{{ $t("message.order.vin_number") }}</td>
              <td>
                <template v-if="canBeEdited">
                  <input
                    class="regular full bordered"
                    type="text"
                    placeholder="full VIN number"
                    name="vin-number"
                    list="customer-vins"
                    autocomplete="off"
                    v-model.lazy="orderToEdit.vin"
                  />
                  <!-- VINs de órdenes previas del cliente, como sugerencias -->
                  <datalist id="customer-vins">
                    <option
                      v-for="item in customerVins"
                      :key="item.vin"
                      :value="item.vin"
                    >
                      {{ item.lastUsed ? item.lastUsed.substring(0, 10) : "" }}
                    </option>
                  </datalist>
                </template>
                <template v-else>
                  {{ orderToEdit.vin || $t("message.order.not_provided") }}
                </template>
              </td>
            </tr>
            <tr v-if="!isQuote">
              <td>{{ $t("message.order.facturacion_automatica") }}</td>
              <td>
                <label class="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="orderToEdit.facturacionAutomatica"
                    :true-value="'1'"
                    :false-value="'0'"
                    class="form-checkbox h-5 w-5 text-blue-600 rounded"
                    v-if="canBeEdited"
                  />
                  <span class="ml-2">{{
                    orderToEdit.facturacionAutomatica === "1" ||
                    orderToEdit.facturacionAutomatica === true
                      ? "Sí"
                      : "No"
                  }}</span>
                </label>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.order.payment_terms") }}
              </td>
              <td>
                <select
                  v-if="canBeEdited"
                  name="credit-retail"
                  class="regular full bordered"
                  v-model.lazy="orderToEdit.creditRetail"
                >
                  <option value="CONT">Contado</option>
                  <option value="CRED">Crédito</option>
                </select>
                <template v-else>
                  {{
                    orderToEdit.creditRetail === "CRED" ? "Crédito" : "Contado"
                  }}
                </template>
              </td>
            </tr>
            <tr>
              <td>{{ $t("message.order.created_by") }}</td>
              <td>{{ orderToEdit.createdBy }}</td>
            </tr>
            <tr>
              <td>{{ $t("message.order.creation_flow") }}</td>
              <td>{{ orderToEdit.creationFlow }}</td>
            </tr>
          </tbody>
        </table>
        <!-- Cliente Infoauto (editable) -->
        <div
          v-if="canBeEdited && orderToEdit.customerInfoauto"
          class="customer-card"
        >
          <div class="customer-card-head">
            <i class="mdi mdi-account-circle customer-avatar"></i>
            <div class="customer-identity">
              <span class="customer-eyebrow">
                {{ $t("message.order.infoauto_customer_selected") }}
              </span>
              <a
                v-if="orderToEdit.customerInfoauto.generalInfo.infoautoId"
                class="customer-name customer-link"
                :href="
                  infoautoCustomerURL(orderToEdit.customerInfoauto.generalInfo)
                "
                target="_blank"
                rel="noopener"
                :title="$t('message.quotes_page.open_customer')"
              >
                {{ orderToEdit.customerInfoauto.generalInfo.name }}
                <i class="mdi mdi-open-in-new"></i>
              </a>
              <span v-else class="customer-name">
                {{ orderToEdit.customerInfoauto.generalInfo.name }}
              </span>
              <span
                v-if="orderToEdit.customerInfoauto.generalInfo.email"
                class="customer-email"
              >
                {{ orderToEdit.customerInfoauto.generalInfo.email }}
              </span>
            </div>
            <button
              type="button"
              class="remove-customer"
              :title="$t('message.order.remove')"
              @click="removeInfoautoCustomer()"
            >
              <i class="mdi mdi-close"></i>
              <span>{{ $t("message.order.remove") }}</span>
            </button>
          </div>

          <div v-if="orderToEdit.customerInfoauto.credit" class="credit-info">
            <div
              class="credit-type"
              :class="
                orderToEdit.customerInfoauto.credit.diasCredito > 0
                  ? 'is-credit'
                  : 'is-cash'
              "
            >
              <i
                class="mdi"
                :class="
                  orderToEdit.customerInfoauto.credit.diasCredito > 0
                    ? 'mdi-credit-card-outline'
                    : 'mdi-cash'
                "
              ></i>
              {{
                orderToEdit.customerInfoauto.credit.diasCredito > 0
                  ? $t("message.order.credit_client")
                  : $t("message.order.cash_paying_customer")
              }}
            </div>
            <dl class="credit-grid">
              <div>
                <dt>{{ $t("message.order.credit_limit") }}</dt>
                <dd>
                  {{ currency
                  }}{{
                    orderToEdit.customerInfoauto.credit.mtoLimCia
                      | currencyFormatted
                  }}
                </dd>
              </div>
              <div v-if="orderToEdit.customerInfoauto.credit.mtoDispCia > 0">
                <dt>{{ $t("message.order.available_credit") }}</dt>
                <dd class="is-positive">
                  {{ currency
                  }}{{
                    orderToEdit.customerInfoauto.credit.mtoDispCia
                      | currencyFormatted
                  }}
                </dd>
              </div>
              <div v-if="orderToEdit.customerInfoauto.credit.diasCredito !== 0">
                <dt>{{ $t("message.order.credit_days") }}</dt>
                <dd>{{ orderToEdit.customerInfoauto.credit.diasCredito }}</dd>
              </div>
              <div v-if="orderToEdit.customerInfoauto.credit.mtoUsaCia !== 0">
                <dt>{{ $t("message.order.total_debt") }}</dt>
                <dd class="is-negative">
                  {{ currency
                  }}{{
                    orderToEdit.customerInfoauto.credit.mtoUsaCia
                      | currencyFormatted
                  }}
                </dd>
              </div>
              <div v-if="orderToEdit.customerInfoauto.credit.mtoVencCia !== 0">
                <dt>{{ $t("message.order.overdue_amount") }}</dt>
                <dd class="is-negative">
                  {{ currency
                  }}{{
                    orderToEdit.customerInfoauto.credit.mtoVencCia
                      | currencyFormatted
                  }}
                </dd>
              </div>
              <div
                v-if="orderToEdit.customerInfoauto.credit.diasDeudaCia !== 0"
              >
                <dt>{{ $t("message.order.debt_days") }}</dt>
                <dd class="is-negative">
                  {{ orderToEdit.customerInfoauto.credit.diasDeudaCia }}
                </dd>
              </div>
            </dl>
          </div>
          <div v-else class="credit-missing">
            {{ $t("message.order.credit_info") }}: —
          </div>
        </div>

        <!-- Cliente Infoauto (solo lectura) -->
        <div
          v-if="!canBeEdited && order.customerInfoauto"
          class="customer-card"
        >
          <div class="customer-card-head">
            <i class="mdi mdi-account-circle customer-avatar"></i>
            <div class="customer-identity">
              <span class="customer-eyebrow">
                {{ $t("message.order.infoauto_customer") }}
              </span>
              <a
                v-if="order.customerInfoauto.generalInfo.infoautoId"
                class="customer-name customer-link"
                :href="infoautoCustomerURL(order.customerInfoauto.generalInfo)"
                target="_blank"
                rel="noopener"
                :title="$t('message.quotes_page.open_customer')"
              >
                {{ order.customerInfoauto.generalInfo.name }}
                <i class="mdi mdi-open-in-new"></i>
              </a>
              <span v-else class="customer-name">
                {{ order.customerInfoauto.generalInfo.name }}
              </span>
              <span
                v-if="order.customerInfoauto.generalInfo.email"
                class="customer-email"
              >
                {{ order.customerInfoauto.generalInfo.email }}
              </span>
            </div>
          </div>
          <dl class="credit-grid">
            <div>
              <dt>Price level</dt>
              <dd>
                {{ order.customerInfoauto.generalInfo.priceLevel || "—" }}
              </dd>
            </div>
            <div>
              <dt>Tax</dt>
              <dd>
                {{
                  order.customerInfoauto.generalInfo.tax
                    ? $t("message.order.taxable")
                    : $t("message.order.no_taxable")
                }}
              </dd>
            </div>
          </dl>
          <p
            v-if="order.customerInfoauto.generalInfo.notes"
            class="customer-notes"
          >
            <i class="mdi mdi-note-text-outline"></i>
            {{ order.customerInfoauto.generalInfo.notes }}
          </p>
        </div>

        <!-- Impuestos / resale tax -->
        <div
          v-if="order.customer || order.customerInfoauto"
          class="tax-status"
          :class="customerTaxable ? 'is-taxable' : 'is-exempt'"
        >
          <div class="tax-status-text">
            <span class="tax-title">
              <i
                class="mdi"
                :class="
                  customerTaxable ? 'mdi-receipt' : 'mdi-shield-check-outline'
                "
              ></i>
              {{
                customerTaxable
                  ? $t("message.order.this_customer_is_taxable")
                  : $t("message.order.this_customer_is_not_taxable")
              }}
            </span>
            <span class="tax-detail">
              {{
                order.validResaleTax.length > 0
                  ? $t("message.order.valid_resale_tax")
                  : $t("message.order.no_valid_resale_tax")
              }}
            </span>
          </div>
          <button
            type="button"
            class="tax-manage"
            @click="showManageResaleTax = true"
          >
            {{ $t("message.order.manage_resale_tax") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="box">
    <div class="box-header">
      <h6 class="title">
        {{ $t("message.order.general_info") }}
      </h6>
    </div>
    <div class="box-content-block">
      <p>{{ $t("message.order.no_order_selected") }}</p>
    </div>
  </div>
</template>
<script>
import { updateOrder } from "@/api/store/OrdersService.js";
import {
  getCustomers as getCustomersApi,
  getCustomerVins
} from "@/api/store/CustomersService.js";
import NewCustomerModal from "@/components/customer/NewCustomerModal";
import ManageResaleTaxModal from "@/components/customer/ManageResaleTaxModal";
import InfoautoCustomerFinder from "../customer/InfoautoCustomerFinder.vue";
import { mapGetters } from "vuex";
import { resolveStoreApiUrl } from "@/utils/store-api-url";
import { isQuoteNumber } from "@/api/store/QuotesService.js";

export default {
  components: {
    NewCustomerModal,
    InfoautoCustomerFinder,
    ManageResaleTaxModal
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    editing: {
      type: String,
      required: true
    },
    canBeEdited: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showManageResaleTax: false,
      // Desde el principio (no en mounted): con null el primer render mostraba
      // "sin orden" cada vez que el componente se volvia a montar
      orderToEdit: this.order ? { ...this.order } : null,
      customers: [],
      saving: false,
      showNewCustomerModal: false,
      showInfoautoSearch: false,
      infoautoCustomerSelected: null,
      tempEmail: "",
      // Which link was just copied: "admin" or "customer"
      copiedLink: null,
      customerVins: []
    };
  },
  mounted() {
    this.syncOrder();
    this.loadCustomerVins();
    this.loadWarehouses();
  },
  watch: {
    "order.customerInfoautoId"() {
      this.loadCustomerVins();
    },
    "order.departamentoId"() {
      this.loadWarehouses();
    },
    order: {
      handler() {
        console.log(this.order);
        this.syncOrder();
      },
      deep: true
    },
    orderToEdit: {
      handler(newValue, oldValue) {
        if (
          newValue &&
          oldValue &&
          JSON.stringify(newValue) != JSON.stringify(this.order)
        ) {
          this.saveChanges();
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters("general", ["getStoreOptions", "getDepartments"]),
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    customerTaxable() {
      if (this.order.validResaleTax.length > 0) {
        return false;
      }

      return true;
    },
    // El departamento es el Infoauto donde se factura (las piezas pueden ser de otro):
    // se puede cambiar hasta enviar la orden. El API verifica el envío en nkrsync.
    canChangeDepartment() {
      const closedStatuses = ["Cancelled", "Shipped", "Delivered"];
      return (
        !closedStatuses.includes(this.order.status) &&
        this.order.infoautoStatus !== "migrated"
      );
    },
    // El superadmin corrige el depósito aunque la orden esté enviada: el API
    // solo lo rechaza si ya llegó a Infoauto.
    canChangeWarehouse() {
      return (
        this.canChangeDepartment ||
        (this.currentUser.role == "superadmin" &&
          this.order.infoautoStatus !== "migrated")
      );
    },
    storesAvailable() {
      return this.getDepartments;
    },
    warehouses() {
      return this.$store.getters["general/getDepartmentWarehouses"](
        this.order.departamentoId
      );
    },
    orderWarehouseName() {
      const warehouse = this.warehouses.find(
        w => w.depositoId == this.orderToEdit.warehouseId
      );
      return warehouse ? warehouse.descDeposito : this.orderToEdit.warehouseId;
    },
    currentUserPermissions() {
      return this.$store.getters["auth/currentUserPermissions"];
    },
    currentUser() {
      return this.$store.getters["auth/currentUser"];
    },
    existingCustomer() {
      return (
        this.order.customer &&
        this.orderToEdit.email === this.order.customer.email
      );
    },
    sellers() {
      return this.$store.getters["general/getSellers"];
    },
    // Cotizacion (numero negativo): no esta en la tienda ni se despacha
    isQuote() {
      return isQuoteNumber(this.order.orderNumber);
    },
    // Página imprimible de la tienda, con los datos actuales de la orden.
    // Las versiones puntuales se abren desde la pestaña de versiones.
    orderURL() {
      const store = this.$store.getters["general/getActiveStore"];
      switch (store.code) {
        case "nkr_venezuela":
          // Su tienda no tiene página de impresión: último PDF o la orden.
          if (this.order.invoiceUrl) {
            return resolveStoreApiUrl(this.order.invoiceUrl, store.endpoint);
          }
          return this.orderWebURL;
        default:
          return `${store.url}order/print/${this.order.orderNumber}-${this.order.validator}`;
      }
    },
    // Every store serves its quotes at /quote on its own domain (with the
    // printable order page, or the PDF from this API): the customer never sees
    // the API's domain. From the store's origin, whatever path its url has.
    quoteURL() {
      const store = this.$store.getters["general/getActiveStore"];
      return new URL(
        `/quote/${this.order.orderNumber}-${this.order.validator}`,
        store.url
      ).href;
    },
    orderPackingSlipURL() {
      return `${this.$store.getters["general/getActiveStore"].endpoint}/orders/print_picking_list/${this.order.orderNumber}-${this.order.validator}`;
    },
    orderWebURL() {
      switch (this.$store.getters["general/getActiveStore"].code) {
        case "nkr_venezuela":
          return `${this.$store.getters["general/getActiveStore"].url}orden/${this.order.orderNumber}/${this.order.validator}`;
        default:
          return `${this.$store.getters["general/getActiveStore"].url}order/${this.order.orderNumber}-${this.order.validator}`;
      }
    },
    orderAdminLink() {
      const { storeCode, departmentCode } = this.$route.params;
      const page = this.isQuote
        ? `quotes?orderOpen=${this.order.orderNumber}`
        : `order/${this.order.orderNumber}`;
      return `${window.location.origin}/store/${storeCode}/${departmentCode}/${page}`;
    },
    orderPackingManifestURL() {
      const { storeCode, departmentCode } = this.$route.params;
      return `${window.location.origin}/store/${storeCode}/${departmentCode}/order/${this.order.orderNumber}/packing-manifest`;
    },
    salesman() {
      if (this.sellers.length === 0) {
        return "";
      }
      return (
        this.sellers.find(seller => seller.email == this.order.salesman)
          ?.name ||
        this.sellers.find(seller => seller.defaultSeller === "1")?.name ||
        this.sellers[0].name
      );
    }
  },
  methods: {
    async copyLink(link, which) {
      try {
        await navigator.clipboard.writeText(link);
        this.copiedLink = which;
        this.$toast.open({
          message: this.$t("message.order.link_copied"),
          type: "success",
          position: "top-right"
        });
        setTimeout(() => {
          this.copiedLink = null;
        }, 2000);
      } catch (e) {
        this.$toast.open({
          message: this.$t("message.order.copy_link_error"),
          type: "error",
          position: "top-right"
        });
      }
    },
    resaleTaxCreated() {
      this.showManageResaleTax = false;
      this.$emit("finishedEditing", true);
    },
    // Ficha del cliente en la página de clientes Infoauto (nueva pestaña).
    infoautoCustomerURL(generalInfo) {
      const { storeCode, departmentCode } = this.$route.params;
      return this.$router.resolve({
        name: "InfoautoCustomersPage",
        params: { storeCode, departmentCode },
        query: {
          search: generalInfo.email || generalInfo.name || "",
          ia_open: generalInfo.infoautoId
        }
      }).href;
    },
    removeInfoautoCustomer() {
      console.log("removeinfoautocustomer");
      this.orderToEdit.customerEmail = null;
      this.orderToEdit.customerInfoautoId = null;
      this.orderToEdit.customerInfoauto = null;

      console.log(this.orderToEdit.customerInfoauto);
    },
    selectedInfoautoCustomer(customer) {
      // Close modal immediately to avoid UI feeling stuck while server processes
      this.showInfoautoSearch = false;

      // Wait for the modal to close visually before updating the data
      // This prevents the UI from feeling stuck during server update
      this.$nextTick(() => {
        this.orderToEdit.customerEmail = customer.email1;
        this.orderToEdit.customerInfoautoId = customer.personaId;
        this.orderToEdit.customerInfoauto = {
          generalInfo: {
            name: customer.nombreComp,
            email: customer.email1,
            notes: customer.observaciones
          }
        };
      });
    },
    getCustomers() {
      let filters = {
        page: 1,
        limitPerPage: 10,
        orderCol: "name",
        orderDir: "desc",
        search: this.tempEmail
      };
      getCustomersApi(filters)
        .then(res => {
          this.customers = res.customers;
          console.log(res);
        })
        .catch(err => {
          console.log(err);
        });
    },
    saveChanges() {
      this.$emit("updating");
      this.saving = true;
      const orderUpdates = {
        purchaseOrderNumber: this.orderToEdit.purchaseOrderNumber,
        salesman: this.orderToEdit.salesman,
        customerEmail: this.orderToEdit.customerEmail,
        vin: this.orderToEdit.vin,
        purchase: this.orderToEdit.vin,
        status: this.orderToEdit.status,
        customerInfoautoId: this.orderToEdit.customerInfoautoId,
        storeSource: this.orderToEdit.storeSource,
        departamentoId: this.orderToEdit.departamentoId,
        // Depósito de la tienda; el API usa el por defecto si no es de la tienda
        warehouseId: this.orderToEdit.warehouseId,
        facturacionAutomatica: this.orderToEdit.facturacionAutomatica,
        creditRetail: this.orderToEdit.creditRetail
      };
      updateOrder(this.order.orderNumber, "general-info", orderUpdates)
        .then(res => {
          // this.$toast.open({
          //   message: "Order Updated",
          //   type: "success",
          //   position: "top-right"
          // });
          if (res.warnings) {
            res.warnings.forEach(element => {
              this.$toast.open({
                message: element,
                type: "info",
                position: "top-right",
                duration: 10000
              });
            });
          }
          this.saving = false;
          this.$emit("finishedEditing", true);
          // The new customer has a usual forwarder: the order modal asks to
          // add it (this component is remounted when the order reloads)
          if (res.suggestedForwarder) {
            this.$emit("suggestedForwarder", res.suggestedForwarder);
          }
          this.$emit("updateFinished", this.orderToEdit);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
          this.saving = false;
          // Clears the modal's "updating" (emitted above)
          this.$emit("finishedEditing", false);
        })
        .finally(() => {});
    },
    loadCustomerVins() {
      const infoautoId = this.order.customerInfoautoId;
      if (!infoautoId) {
        this.customerVins = [];
        return;
      }
      getCustomerVins(infoautoId, this.order.orderNumber)
        .then(vins => {
          // Ignore a late response for a customer that is no longer selected
          if (this.order.customerInfoautoId === infoautoId) {
            this.customerVins = vins;
          }
        })
        .catch(() => {
          this.customerVins = [];
        });
    },
    // Depósitos de la tienda de la orden (el que va en la cabecera a Infoauto):
    // quedan en Vuex, no se piden de nuevo en cada recarga de la orden
    loadWarehouses() {
      this.$store.dispatch(
        "general/loadDepartmentWarehouses",
        this.order.departamentoId
      );
    },
    syncOrder() {
      console.log("syncOrder");
      this.orderToEdit = { ...this.order };
      this.tempEmail = this.order.customerEmail;
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

$border: #e5e7eb;
$muted: #6b7280;
$text: #111827;

.header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.header-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  color: $muted;
  transition: background 0.15s, color 0.15s;

  i {
    font-size: 20px;
  }

  &:hover {
    background: #f3f4f6;
    color: $link-color-1;
  }

  &.is-done {
    color: #16a34a;
  }
}

.finder-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 16px;
  font-weight: 500;
  color: $link-color-1;

  &:hover {
    text-decoration: underline;
  }
}

.customer-card {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid $border;
  border-radius: 10px;
  background: #f9fafb;
  font-size: 16px;
}

.customer-card-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.customer-avatar {
  font-size: 34px;
  line-height: 1;
  color: $link-color-1;
}

.customer-identity {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}

.customer-eyebrow {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $muted;
}

.customer-name {
  font-size: 16px;
  font-weight: 600;
  color: $text;
  overflow-wrap: anywhere;
}

.customer-link {
  text-decoration: none;

  i {
    margin-left: 2px;
    font-size: 14px;
    color: $link-color-1;
  }

  &:hover {
    color: $link-color-1;
    text-decoration: underline;
  }
}

.customer-email {
  font-size: 16px;
  color: #4b5563;
  overflow-wrap: anywhere;
}

.remove-customer {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 2px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 500;
  color: #b91c1c;

  &:hover {
    background: #fef2f2;
  }
}

.credit-info {
  margin-top: 10px;
}

.credit-type {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;

  i {
    font-size: 18px;
  }

  &.is-credit {
    background: #dcfce7;
    color: #166534;
  }

  &.is-cash {
    background: #fef3c7;
    color: #92400e;
  }
}

.credit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 8px 12px;
  margin: 10px 0 0;

  dt {
    font-size: 16px;
    color: $muted;
  }

  dd {
    margin: 0;
    font-weight: 600;
    color: $text;
    font-variant-numeric: tabular-nums;

    &.is-positive {
      color: #15803d;
    }

    &.is-negative {
      color: #b91c1c;
    }
  }
}

.credit-missing,
.customer-notes {
  margin: 10px 0 0;
  font-size: 16px;
  color: #4b5563;
}

.customer-notes {
  display: flex;
  gap: 6px;

  i {
    color: $muted;
  }
}

.tax-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid;
  border-radius: 10px;
  font-size: 16px;

  &.is-taxable {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #166534;
  }

  &.is-exempt {
    border-color: #fecaca;
    background: #fef2f2;
    color: #991b1b;
  }
}

.tax-status-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tax-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;

  i {
    font-size: 18px;
  }
}

.tax-detail {
  font-size: 16px;
  opacity: 0.85;
}

.tax-manage {
  height: 32px;
  padding: 0 12px;
  border: 1px solid currentColor;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 600;

  &:hover {
    background: rgba(255, 255, 255, 0.6);
  }
}
</style>
